import type { BehaviorNode } from "../blueprint/types.js";
import { normalizeShortcut } from "../blueprint/shortcut-keys.js";
import type { SceneCustomEventEntry } from "../layout/types.js";
import { getOrCreateCustomEventTriggerState } from "./customEventTriggerState.js";
import { indexGraph } from "./graph.js";
import { buildDependencySignature } from "./renderCache.js";
import { uniqueDependencies } from "./method.js";
import type { RuntimeContext, RuntimeCustomEventTrigger } from "./types.js";

type CompiledCustomEventTriggerPart =
  | {
      type: "onLoad";
      dependencies: string[];
    }
  | {
      type: "onChange";
      dependencies: string[];
    }
  | {
      type: "onControlRoom";
      eventNodeId: string;
      combo: string;
    };

function getEventWatchDependencies(node: BehaviorNode): string[] {
  if (node.type !== "event" || !("kind" in node) || node.kind !== "onChange") {
    return [];
  }

  const source = node.objectSource;
  if (!source) {
    return [];
  }

  const path = node.objectFieldPath;
  return path ? [`${source}.${path}`] : [source];
}

function compileCustomEventTriggerParts(
  entry: SceneCustomEventEntry,
): CompiledCustomEventTriggerPart[] {
  const indexed = indexGraph(entry.triggerGraph);
  const parts: CompiledCustomEventTriggerPart[] = [];

  for (const node of indexed.nodesById.values()) {
    if (node.type !== "event") {
      continue;
    }

    if (!eventCanReachOutput(node, entry.triggerGraph.nodes, indexed)) {
      continue;
    }

    if (node.kind === "onLoad") {
      parts.push({
        type: "onLoad",
        dependencies: [],
      });
    }

    if (node.kind === "onChange") {
      parts.push({
        type: "onChange",
        dependencies: getEventWatchDependencies(node),
      });
    }

    if (node.kind === "onControlRoom") {
      const combo = getControlRoomCombo(node);
      if (combo) {
        parts.push({
          type: "onControlRoom",
          eventNodeId: node.id,
          combo,
        });
      }
    }
  }

  return parts;
}

function getControlRoomCombo(node: BehaviorNode): string | undefined {
  if (node.type !== "event" || node.kind !== "onControlRoom") {
    return undefined;
  }

  const combo = normalizeShortcut(
    node.shortcutKey1 ?? "",
    node.shortcutKey2,
    node.shortcutKey3,
  );
  return combo || undefined;
}

function getFlowOutputPortIds(node: BehaviorNode): string[] {
  return node.ports
    .filter((port) => port.direction === "output" && port.valueType === "flow")
    .map((port) => port.id);
}

function eventCanReachOutput(
  eventNode: BehaviorNode,
  nodes: BehaviorNode[],
  indexed: ReturnType<typeof indexGraph>,
): boolean {
  const outputNodeIds = new Set(
    nodes
      .filter(
        (node) => node.type === "control" && node.controlType === "eventOutput",
      )
      .map((node) => node.id),
  );
  if (outputNodeIds.size === 0) {
    return false;
  }

  const queue = getFlowOutputPortIds(eventNode).map((portId) => ({
    nodeId: eventNode.id,
    portId,
  }));
  const visited = new Set<string>();

  while (queue.length > 0) {
    const current = queue.shift()!;
    const visitKey = `${current.nodeId}:${current.portId}`;
    if (visited.has(visitKey)) {
      continue;
    }
    visited.add(visitKey);

    const connections =
      indexed.outputConnectionsByNodeAndPort.get(visitKey) ?? [];
    for (const connection of connections) {
      if (outputNodeIds.has(connection.toNodeId)) {
        return true;
      }

      const nextNode = indexed.nodesById.get(connection.toNodeId);
      if (!nextNode) {
        continue;
      }

      for (const portId of getFlowOutputPortIds(nextNode)) {
        queue.push({ nodeId: nextNode.id, portId });
      }
    }
  }

  return false;
}

function shouldRunControlRoomTrigger(
  part: Extract<CompiledCustomEventTriggerPart, { type: "onControlRoom" }>,
  ctx: RuntimeContext,
  state: ReturnType<typeof getOrCreateCustomEventTriggerState>,
  phase: "render" | "runtime",
): boolean {
  const matchingDispatches = (ctx.__controlRoomDispatches ?? []).filter(
    (dispatch) =>
      dispatch.combo === part.combo &&
      !ctx.__committedControlRoomDispatchIds?.has(dispatch.id),
  );
  if (matchingDispatches.length === 0) {
    return false;
  }

  if (ctx.__deferControlRoomDispatchConsumption) {
    return true;
  }

  const consumedByEvent =
    state.consumedControlRoomDispatchIdsByPhaseAndEventId ??
    new Map<string, Set<number>>();
  state.consumedControlRoomDispatchIdsByPhaseAndEventId = consumedByEvent;

  const consumedKey = `${phase}:${part.eventNodeId}`;
  const consumedIds = consumedByEvent.get(consumedKey) ?? new Set<number>();
  consumedByEvent.set(consumedKey, consumedIds);

  let shouldRun = false;
  for (const dispatch of matchingDispatches) {
    if (consumedIds.has(dispatch.id)) {
      continue;
    }
    consumedIds.add(dispatch.id);
    shouldRun = true;
  }

  return shouldRun;
}

function compileCustomEventTrigger(
  entry: SceneCustomEventEntry,
): RuntimeCustomEventTrigger {
  const parts = compileCustomEventTriggerParts(entry);
  const dependencies = uniqueDependencies(
    parts.flatMap((part) => ("dependencies" in part ? part.dependencies : [])),
  );

  return {
    id: entry.id,
    name: entry.name,
    dependencies,

    shouldRun(ctx, stateKey, phase) {
      const state = getOrCreateCustomEventTriggerState(ctx, stateKey);
      let shouldRun = false;

      const hasOnLoad = parts.some((part) => part.type === "onLoad");

      const onLoadDoneByPhase = state.onLoadDoneByPhase ?? {};
      state.onLoadDoneByPhase = onLoadDoneByPhase;

      if (hasOnLoad && !onLoadDoneByPhase[phase]) {
        shouldRun = true;
        onLoadDoneByPhase[phase] = true;
        state.onLoadDone = onLoadDoneByPhase.render === true;
      }

      const watchDependencies = uniqueDependencies(
        parts
          .filter((part) => part.type === "onChange")
          .flatMap((part) => part.dependencies),
      );

      if (watchDependencies.length > 0) {
        const nextSignature = buildDependencySignature(ctx, watchDependencies);
        const signatures = state.lastSignatureByPhase ?? new Map();
        state.lastSignatureByPhase = signatures;
        const previousSignature = signatures.get(phase);

        if (previousSignature != null && previousSignature !== nextSignature) {
          shouldRun = true;
        }

        signatures.set(phase, nextSignature);
        state.lastSignature = nextSignature;
      }

      for (const part of parts) {
        if (part.type !== "onControlRoom") {
          continue;
        }

        if (shouldRunControlRoomTrigger(part, ctx, state, phase)) {
          shouldRun = true;
        }
      }

      return shouldRun;
    },
  };
}

/**
 * Compiles scene catalog custom events into runtime triggers with onLoad/onChange semantics.
 */
export function compileSceneCustomEvents(
  entries: SceneCustomEventEntry[] = [],
): {
  dependencies: string[];
  customEventTriggers: Record<string, RuntimeCustomEventTrigger>;
  customEventTriggersByName: Record<string, RuntimeCustomEventTrigger>;
} {
  const customEventTriggers: Record<string, RuntimeCustomEventTrigger> = {};
  const customEventTriggersByName: Record<string, RuntimeCustomEventTrigger> =
    {};
  const dependencyBuckets: string[][] = [];

  for (const entry of entries) {
    const trigger = compileCustomEventTrigger(entry);

    customEventTriggers[entry.id] = trigger;
    customEventTriggersByName[entry.name] = trigger;
    customEventTriggersByName[entry.name.toLocaleLowerCase()] = trigger;
    dependencyBuckets.push(trigger.dependencies);
  }

  return {
    dependencies: uniqueDependencies(dependencyBuckets.flat()),
    customEventTriggers,
    customEventTriggersByName,
  };
}
